import { useEffect, useState } from "react";
import { deleteApi, getApi, postApi } from "../../../../utils/services";
import { PAYROLL_SUMMARY_IMPORTS, UPLOAD_EMPLOYEE_PAYROLL_SUMMARY } from "../../../../utils/services/apiPath";
import { useParams } from "react-router-dom";
import { ReactSpreadsheetImport, StepType } from "react-spreadsheet-import";
import { Button } from "@mui/material";
import { UploadFile } from "@mui/icons-material";
import {
  DataGridPro,
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
  GridToolbarDensitySelector,
  GridToolbarExport
} from "@mui/x-data-grid-pro";
import { errorToast, successToast } from "../../../../utils/toastHelper";
import { useSelector } from "react-redux";
import { subModPermissionSelector } from "../../../store/features/permissions/permissionsSlice";
import { R_PROJECTS } from "../../../../utils/permissionReferenceConfig";

export function ImportGridCustomToolbar({ canExport }) {
  return (
    <GridToolbarContainer>
      <GridToolbarColumnsButton />
      <GridToolbarFilterButton />
      <GridToolbarDensitySelector />
      {canExport && <GridToolbarExport />}
    </GridToolbarContainer>
  );
}

const headerNames =
  [
    "firstName",
    "grossAmountEarned",
    "lastName",
    "medicareEmployee",
    "other",
    "overtimeHours",
    "overtimePayRate",
    "overtimeTotal",
    "perDiem",
    "position",
    "regularHours",
    "regularTotal",
    "socialSecurityEmployee",
    "stateWitholdingTax",
    "totalHours",
    "totalPay",
    "weekEndingDate",
    "regularPayRate",
    "middleInitial",
    "fica",
    "queriedAt",
    "fedWithHoldingTax",
    "stateWhTax",
    "netWagePaidForWeek",
    "explanation",
    "exception",
    "otherDeduction1",
    "otherDeduction2",
    "totalDeductions",
    "otherDeduction1Explanation",
    "otherDeduction2Explanation"
  ]

const fields = headerNames.map(fieldName => ({
  label: fieldName,
  key: fieldName,
  fieldType: {
    type: "input"
  }
}));

const UploadActionCell = ({ onClick }) => {
  return (
    <div>
      <Button variant="contained" startIcon={<UploadFile />} onClick={onClick}>Import</Button>
    </div>
  )
}

const AvailablePayrollImports = () => {
  const [availablePayrollSummaries, setAvailablePayrollSummaries] = useState([])
  const [selectedPayrollSummary, setSelectedPayrollSummary] = useState([])
  const [isOpen, setIsOpen] = useState(false)
  const [deleteImportId, setDeleteImportId] = useState("")
  const { id } = useParams();
  const { canExport, canImport } = useSelector(subModPermissionSelector(R_PROJECTS, "Available Payroll Imports"));

  const handleClick = (importId) => {
    getSelectedPayrollSummary(importId)
    setIsOpen(true)
    setDeleteImportId(importId)
  }

  const columns = [
    {
      field: "fileName",
      headerName: "File Name",
        flex: 1
    },
    {
      field: "clientImportId",
      headerName: "Actions",
      renderCell: params => <UploadActionCell params={params} onClick={() => handleClick(params.row.clientImportId)} />,
      width: 150,
    },
  ].filter(col => (col.field === "clientImportId" ? canImport : true))

  const getAvailablePayrollSummaries = async () => {
    const url = PAYROLL_SUMMARY_IMPORTS.replace("{projectId}", id)
    const { processedFiles } = await getApi(url)
    setAvailablePayrollSummaries(processedFiles)
  }

  function stringifyValuesInArray(arr) {
    if (!Array.isArray(arr)) {
      return arr;
    }

    return arr.map(obj => stringifyValues(obj));
  }

  function stringifyValues(obj) {
    if (typeof obj !== 'object' || obj === null) {
      return String(obj);
    }

    if (Array.isArray(obj)) {
      return obj.map(item => stringifyValues(item));
    }

    const newObj = {};
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        newObj[key] = stringifyValues(obj[key]);

      }
    }
    return newObj;
  }

  const getSelectedPayrollSummary = async (importId) => {
    let url = PAYROLL_SUMMARY_IMPORTS.replace("{projectId}", id)
    const { data } = await getApi(`${url}/processed/${importId}`)
    if (data) {
      const values = data.map(d => Object.values(d))

      const processed = stringifyValuesInArray(values)
      setSelectedPayrollSummary(processed)
    }
  }

  const deletePayrollSummary = async () => {
    let url = PAYROLL_SUMMARY_IMPORTS.replace("{projectId}", id)
    try {
      await deleteApi(`${url}/${deleteImportId}`)
      setSelectedPayrollSummary([])
      getAvailablePayrollSummaries()
    } catch (e) {
      errorToast("Unable to mark payroll summary as completed")
    }
  }

  const onClose = () => {
    setIsOpen(false)
  }

  useEffect(() => {
    if (id) getAvailablePayrollSummaries()
  }, [])

  const onSubmit = async (data, file) => {
    try {
      const url = UPLOAD_EMPLOYEE_PAYROLL_SUMMARY.replace('{projectId}', id)
      const response = await postApi(url, {
        fileModel: {
          base64String: "",
          fileName: ""
        },
        data: data?.validData
      })

      if (response?.error) {
        throw new Error()
      } else {
        deletePayrollSummary()
      }
      successToast("Successfuly imported payroll summary")
    } catch (e) {
      return errorToast("Failed to import file")
    }
  }

  return (
    <div className="rs-grid-wrapper v2" style={{ display: "flex", flexDirection: "column" }}>
      <DataGridPro
        classes={{
          cell: "proGrid-table-cell"
        }}
        sortingOrder={["desc", "asc"]}
        columnHeaderHeight={44}
        rowHeight={44}
        columns={columns}
        initialState={{
          pagination: {
            paginationModel: {
              pageSize: 10
            }
          }
        }}
        slots={{
          toolbar: () => <ImportGridCustomToolbar canExport={canExport} />,
          // noResultsOverlay: NoResultsOverlay
        }}
        slotProps={{ toolbar: { showQuickFilter: true } }}
        rows={availablePayrollSummaries}
        getRowId={row => row.clientImportId}
        pagination
      // groupingColDef={groupingColDef}
      />
      {selectedPayrollSummary.length > 0 &&
        <ReactSpreadsheetImport
          isOpen={isOpen}
          onClose={onClose}
          fields={fields}
          initialStepState={{
            type: StepType.matchColumns,
            data: selectedPayrollSummary,
            headerValues: headerNames
          }}
          customTheme={{
            rsi: {
              overflow: "auto !important"
            }
          }}
          onSubmit={onSubmit}
        />
      }
    </div>
  )
}

export default AvailablePayrollImports